import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";

export interface CookiePrefs {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = "cookie-consent-v1";

export function getCookiePrefs(): CookiePrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function saveCookiePrefs(p: CookiePrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  window.dispatchEvent(new CustomEvent("cookie-prefs-changed", { detail: p }));
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getCookiePrefs()) setOpen(true);
    const handler = () => setOpen(true);
    window.addEventListener("open-cookie-prefs", handler);
    return () => window.removeEventListener("open-cookie-prefs", handler);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    saveCookiePrefs({ necessary: true, analytics: true, marketing: true });
    setOpen(false);
  };
  const rejectAll = () => {
    saveCookiePrefs({ necessary: true, analytics: false, marketing: false });
    setOpen(false);
  };
  const saveCustom = () => {
    saveCookiePrefs({ necessary: true, analytics, marketing });
    setOpen(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 animate-fade-in">
      <Card className="max-w-3xl mx-auto p-4 sm:p-5 bg-card/95 backdrop-blur-xl border-primary/30 shadow-glow">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-sans text-base font-semibold leading-snug mb-1">{t("cookies.title")}</h3>
            <p className="text-xs text-muted-foreground mb-3">{t("cookies.intro")}</p>

            {showCustomize && (
              <div className="space-y-2 mb-3 p-3 rounded-md bg-background/50 border border-border/40">
                <Row label={t("cookies.necessary")} desc={t("cookies.necessaryDesc")} checked disabled />
                <Row label={t("cookies.analytics")} desc={t("cookies.analyticsDesc")} checked={analytics} onChange={setAnalytics} />
                <Row label={t("cookies.marketing")} desc={t("cookies.marketingDesc")} checked={marketing} onChange={setMarketing} />
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {!showCustomize ? (
                <>
                  <Button size="sm" onClick={acceptAll} className="bg-gradient-primary text-primary-foreground">{t("cookies.acceptAll")}</Button>
                  <Button size="sm" variant="outline" onClick={rejectAll}>{t("cookies.rejectAll")}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCustomize(true)}>{t("cookies.customize")}</Button>
                </>
              ) : (
                <>
                  <Button size="sm" onClick={saveCustom} className="bg-gradient-primary text-primary-foreground">{t("cookies.savePrefs")}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCustomize(false)}>{t("common.cancel")}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, desc, checked, onChange, disabled }: { label: string; desc: string; checked: boolean; onChange?: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[11px] text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
